import { action, computed, makeObservable, observable } from 'mobx';
import { getCurrency } from '../../../../store/constants';
import { RequireAllProps, StringDto } from '../../../../store/utils/Dto';
import extractValue, {
  type EventOrValue,
} from '../../../../store/utils/extractValue';
import Indicator from '../../../../store/utils/Indicator';
import Validator from '../../../../store/utils/Validator';

export default class Sellable<TDto extends SellableDto = SellableDto> {
  archived = false;
  currency = 'USD';
  fulfillmentEmailTemplate = '';
  id = '';
  name = '';
  price = 0;
  sku = '';
  tags = '';

  forceEditMode = false;
  isSkuChanged = false;
  isChanged = false;
  savingAction: SavingAction = 'update';

  readonly type: '' | 'bundle' | 'product' = '';
  readonly indicator = new Indicator();

  protected readonly validator = new Validator(
    this,
    (this.constructor as any).Schema,
  );

  constructor(
    { forceEditMode, ...dto }: SellableConstructorDto = {},
    protected readonly onSave?: (sellable: Sellable<TDto>) => Promise<void>,
  ) {
    makeObservable(this, {
      currency: observable,
      name: observable,
      price: observable,
      sku: observable,
      fulfillmentEmailTemplate: observable,

      forceEditMode: observable,
      isSkuChanged: observable,
      isChanged: observable,

      displayCurrency: computed,
      displayName: computed,
      displayPrice: computed,
      errors: computed,
      allErrorsAsStrings: computed,
      isValid: computed,
      isNew: computed,
      isFullEditAllowed: computed,

      save: action.bound,
      archive: action.bound,
      handleChange: action.bound,
      handleBlur: action.bound,
      updateAfterCreation: action.bound,
      validate: action.bound,
      setSavingAction: action.bound,
      setIsChanged: action.bound,
    });

    this.forceEditMode = Boolean(forceEditMode);
    this.isSkuChanged = Boolean(dto.sku);
  }

  get allErrorsAsStrings(): string[] {
    return [...this.validator.allErrorsAsStrings, this.indicator.error].filter(
      Boolean,
    );
  }

  get displayCurrency(): string {
    return getCurrency(this.currency).char || '';
  }

  get displayName() {
    return this.name;
  }

  get displayPrice() {
    return `${this.displayCurrency}${this.price || 0}`;
  }

  get errors(): StringDto<TDto> {
    let fieldErrors = this.validator.errorsForEnabledFields as any;

    if (this.indicator.error?.includes('sku ')) {
      fieldErrors = { ...fieldErrors, sku: this.indicator.error };
    }

    return fieldErrors;
  }

  get isFullEditAllowed() {
    return this.isNew || this.forceEditMode;
  }

  get isNew() {
    return this.savingAction === 'create';
  }

  get isValid(): boolean {
    return this.validator.isValid;
  }

  async save() {
    this.validate();

    if (!this.onSave) {
      return undefined;
    }

    return this.indicator
      .spyOnPromise(this.onSave(this))
      .then(() => this.setIsChanged(false));
  }

  async archive() {
    this.setSavingAction('archive');
    return this.save();
  }

  handleChange(e: EventOrValue<keyof SellableDto>) {
    const { name, value } = extractValue(e);

    if (name === 'name') {
      this.onNameChanged(value);
    } else if (name === 'sku') {
      this.onSkuChanged(value);
    } else {
      Object.assign(this, { [name]: value });
    }

    this.validator.validate();

    this.isChanged = true;
    this.indicator.setState('new');
  }

  handleBlur(e: EventOrValue<keyof SellableDto>) {
    const { name } = extractValue(e);
    this.validator.enableFieldValidation(name);
  }

  validate(): string[] {
    this.validator.validate();
    return this.validator.allErrorsAsStrings;
  }

  toJSON(): RequireAllProps<SellableDto> {
    return {
      archived: this.archived || undefined,
      currency: this.currency,
      fulfillmentEmailTemplate: this.fulfillmentEmailTemplate || undefined,
      id: this.id,
      name: this.name.trim(),
      price: Number(this.price),
      sku: this.sku,
      tags: this.tags || undefined,
    };
  }

  updateAfterCreation(dto: TDto) {
    this.id = dto.id || '';
    this.savingAction = 'update';
  }

  setIsChanged(value: boolean) {
    this.isChanged = value;
  }

  setSavingAction(savingAction: SavingAction) {
    this.savingAction = savingAction;
  }

  private onNameChanged(value: string) {
    this.name = value;

    if (!this.isSkuChanged) {
      this.sku = formatSku(value);
    }
  }

  private onSkuChanged(value: string) {
    this.isSkuChanged = true;
    this.sku = this.forceEditMode ? value : formatSku(value);
  }
}

export function formatSku(dirtyValue: unknown): string {
  if (typeof dirtyValue !== 'string') {
    return '';
  }

  return dirtyValue.toLowerCase().replaceAll(/\W+/g, '-');
}

export interface SellableDto {
  archived?: boolean;
  currency?: string;
  fulfillmentEmailTemplate?: string;
  id?: string;
  name?: string;
  price?: number | string;
  sku?: string;
  tags?: string;
}

export interface SellableConstructorDto extends SellableDto {
  forceEditMode?: boolean;
}

export type SavingAction = 'archive' | 'create' | 'update';
